import React, { FC } from 'react';
import { t } from '@lingui/macro';

import { CarouselRightSmall, Megaphone } from '@rover/icons';
import { Box, Flex, Text } from '@rover/kibble/core';
import { useTheme } from '@rover/kibble/themes';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

type FullWidthLinkProps = {
  children: React.ReactNode;
  href: string;
  onClick?: () => void;
};

const FullWidthLink: FC<FullWidthLinkProps> = ({ href, onClick, children }) => (
  // We can't use a Kibble Link here due to the custom styles needed - we can't make it full width
  <Flex as="a" href={href} onClick={onClick} alignItems="center" width="100%" px="3x" py="4x">
    {children}
  </Flex>
);

const MobilePromoteYourProfileLink: FC = () => {
  const theme = useTheme();

  const { current: countryConfiguration } = useCountryConfigurationShim();
  const formattedReferralGiftcardAmount =
    countryConfiguration?.formattedReferralGiftcardAmount ?? '$20';

  const fireClickEvent = (): void => {
    fireDataLayerEvent({
      event: 'more-menu-click-sitter-promo-navigation',
    });
  };

  return (
    <Box borderTop={`1px solid ${theme.colors.border.primary}`}>
      <FullWidthLink href="/provider-profile/promote/" onClick={fireClickEvent}>
        <Flex
          alignItems="center"
          width="100%"
          gap="2x"
          justifyContent="space-between"
          sx={{
            '& > *': {
              color: theme.textColor.navigation.toString(),
              textDecoration: 'none',
            },
            ':hover > *': {
              color: theme.textColor.navigation_hover.toString(),
              textDecoration: 'underline',
            },
          }}
        >
          <Flex alignItems="center" gap="2x">
            <Megaphone
              width={theme.space['6x']}
              height={theme.space['6x']}
              aria-label={t`Megaphone icon`}
              style={{
                fill: theme.textColor.navigation.toString(),
              }}
            />
            <Flex flexDirection="column" flexGrow="1" justifyContent="space-between">
              <Text size="200" fontWeight="semibold" pb="1x">
                {t`Promote your profile`}
              </Text>
              <Text size="100">
                {t`Give pet parents a ${formattedReferralGiftcardAmount} credit to try Rover`}
              </Text>
            </Flex>
          </Flex>
          <Box display="flex" justifyContent="flex-end">
            <CarouselRightSmall
              width={theme.space['6x']}
              height={theme.space['6x']}
              aria-label={t`Carousel Right icon`}
              fill={theme.textColor.navigation.toString()}
            />
          </Box>
        </Flex>
      </FullWidthLink>
    </Box>
  );
};

export default MobilePromoteYourProfileLink;
